/**
 * Alta y administración del personal (usuario + rol + PIN/contraseña + a qué
 * sucursales tiene acceso). Sólo la ve el dueño.
 */
import { useEffect, useState } from 'react';
import { api } from '../lib/api';

interface Sucursal {
  id: number;
  nombre: string;
  tipo: 'restaurante' | 'abarrotes';
}

type Rol = 'dueno' | 'cajero' | 'cocina' | 'mesero' | 'repartidor';

interface Persona {
  id: number;
  nombre: string;
  usuario: string;
  rol: Rol;
  telefono: string | null;
  activo: 0 | 1;
  ultimo_acceso: string | null;
  tiene_pin: 0 | 1;
  tiene_password: 0 | 1;
  sucursales: number[];
}

const ETIQUETA_ROL: Record<Rol, string> = {
  dueno: 'Dueño',
  cajero: 'Cajero',
  cocina: 'Cocina',
  mesero: 'Mesero',
  repartidor: 'Repartidor',
};

function ChecksSucursales({
  sucursales,
  seleccion,
  alCambiar,
}: {
  sucursales: Sucursal[];
  seleccion: number[];
  alCambiar: (ids: number[]) => void;
}) {
  function alternar(id: number) {
    alCambiar(seleccion.includes(id) ? seleccion.filter((s) => s !== id) : [...seleccion, id]);
  }

  return (
    <div className="flex flex-wrap gap-2">
      {sucursales.map((s) => (
        <label
          key={s.id}
          className={`flex cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${
            seleccion.includes(s.id)
              ? 'border-nopal bg-nopal-claro text-nopal-oscuro'
              : 'border-salvia/20 text-salvia'
          }`}
        >
          <input
            type="checkbox"
            checked={seleccion.includes(s.id)}
            onChange={() => alternar(s.id)}
            className="accent-nopal"
          />
          {s.nombre}
        </label>
      ))}
    </div>
  );
}

function FormularioAlta({ sucursales, alCrear }: { sucursales: Sucursal[]; alCrear: () => void }) {
  const [nombre, setNombre] = useState('');
  const [rol, setRol] = useState<Rol>('mesero');
  const [telefono, setTelefono] = useState('');
  const [sucursalesSel, setSucursalesSel] = useState<number[]>([]);
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function crear(e: React.FormEvent) {
    e.preventDefault();
    if (!nombre.trim() || !sucursalesSel.length) return;

    try {
      setGuardando(true);
      setError(null);
      await api('/usuarios', {
        method: 'POST',
        body: {
          nombre: nombre.trim(),
          rol,
          telefono: telefono.trim() || undefined,
          sucursales: sucursalesSel,
          ...(rol === 'dueno' ? { password } : { pin }),
        },
      });
      setNombre('');
      setTelefono('');
      setSucursalesSel([]);
      setPassword('');
      setPin('');
      alCrear();
    } catch (err: any) {
      setError(err?.message || 'No se pudo dar de alta al personal.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <form onSubmit={crear} className="mb-6 rounded-2xl border border-nopal/30 bg-white p-5 shadow-sm">
      <h3 className="font-titulo text-base font-bold text-tinta mb-4">Dar de alta personal</h3>
      {error && <p className="mb-3 text-sm font-semibold text-barro-oscuro">{error}</p>}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-bold text-salvia">Nombre *</label>
          <input
            type="text"
            required
            placeholder="Ej. Juan Pérez"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            className="campo text-base"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-bold text-salvia">Rol *</label>
          <select value={rol} onChange={(e) => setRol(e.target.value as Rol)} className="campo text-base">
            {Object.entries(ETIQUETA_ROL).map(([valor, etiqueta]) => (
              <option key={valor} value={valor}>
                {etiqueta}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-bold text-salvia">Teléfono (opcional)</label>
          <input
            type="text"
            placeholder="10 dígitos"
            value={telefono}
            onChange={(e) => setTelefono(e.target.value)}
            className="campo text-base"
          />
        </div>
        {rol === 'dueno' ? (
          <div>
            <label className="mb-1 block text-xs font-bold text-salvia">Contraseña *</label>
            <input
              type="password"
              required
              minLength={6}
              placeholder="Mínimo 6 caracteres"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="campo text-base"
            />
          </div>
        ) : (
          <div>
            <label className="mb-1 block text-xs font-bold text-salvia">PIN *</label>
            <input
              type="text"
              required
              inputMode="numeric"
              placeholder="4 a 6 dígitos"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              className="campo text-base"
            />
          </div>
        )}
      </div>

      <div className="mt-4">
        <label className="mb-1 block text-xs font-bold text-salvia">Acceso a sucursales *</label>
        <ChecksSucursales sucursales={sucursales} seleccion={sucursalesSel} alCambiar={setSucursalesSel} />
      </div>

      <div className="mt-4 flex justify-end">
        <button disabled={guardando} type="submit" className="btn-principal text-sm py-2 px-6">
          {guardando ? 'Guardando…' : 'Guardar Personal'}
        </button>
      </div>
    </form>
  );
}

function FilaPersonal({
  persona,
  sucursales,
  alCambiar,
}: {
  persona: Persona;
  sucursales: Sucursal[];
  alCambiar: () => void;
}) {
  const [editando, setEditando] = useState(false);
  const [rol, setRol] = useState<Rol>(persona.rol);
  const [sucursalesSel, setSucursalesSel] = useState<number[]>(persona.sucursales);
  const [nuevaClave, setNuevaClave] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function guardar() {
    try {
      setGuardando(true);
      setError(null);
      await api(`/usuarios/${persona.id}`, {
        method: 'PATCH',
        body: { rol, sucursales: sucursalesSel },
      });
      setEditando(false);
      alCambiar();
    } catch (err: any) {
      setError(err?.message || 'No se pudo actualizar.');
    } finally {
      setGuardando(false);
    }
  }

  async function alternarActivo() {
    try {
      await api(`/usuarios/${persona.id}`, { method: 'PATCH', body: { activo: persona.activo ? 0 : 1 } });
      alCambiar();
    } catch (err: any) {
      alert(err?.message || 'No se pudo cambiar el estado.');
    }
  }

  async function restablecerClave() {
    if (!nuevaClave) return;
    try {
      setGuardando(true);
      setError(null);
      const ruta = persona.rol === 'dueno' ? 'password' : 'pin';
      await api(`/usuarios/${persona.id}/${ruta}`, { method: 'POST', body: { [ruta]: nuevaClave } });
      setNuevaClave('');
      alert(persona.rol === 'dueno' ? 'Contraseña actualizada.' : 'PIN actualizado.');
    } catch (err: any) {
      setError(err?.message || 'No se pudo restablecer.');
    } finally {
      setGuardando(false);
    }
  }

  const nombresSucursales = sucursales.filter((s) => persona.sucursales.includes(s.id)).map((s) => s.nombre);

  return (
    <div className="rounded-2xl border border-salvia/20 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <p className="font-titulo text-base font-bold text-tinta">{persona.nombre}</p>
            <span className="rounded-full bg-nopal-claro px-2 py-0.5 text-xs font-bold text-nopal-oscuro">
              {ETIQUETA_ROL[persona.rol]}
            </span>
            {!persona.activo && (
              <span className="rounded-full bg-barro/20 px-2 py-0.5 text-xs font-bold text-barro-oscuro">
                Inactivo
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-salvia">{nombresSucursales.join(' · ') || 'Sin sucursales'}</p>
        </div>
        <div className="flex shrink-0 gap-2">
          <button
            onClick={() => setEditando(!editando)}
            className="rounded-lg border border-salvia/20 px-3 py-1.5 text-xs font-bold text-salvia hover:bg-cal"
          >
            {editando ? 'Cancelar' : 'Editar'}
          </button>
          <button
            onClick={alternarActivo}
            className={`rounded-lg border px-3 py-1.5 text-xs font-bold ${
              persona.activo
                ? 'border-barro/30 text-barro-oscuro hover:bg-barro-claro'
                : 'border-nopal/30 text-nopal-oscuro hover:bg-nopal-claro'
            }`}
          >
            {persona.activo ? 'Desactivar' : 'Activar'}
          </button>
        </div>
      </div>

      {editando && (
        <div className="mt-4 space-y-3 border-t border-salvia/10 pt-4">
          {error && <p className="text-sm font-semibold text-barro-oscuro">{error}</p>}
          <div>
            <label className="mb-1 block text-xs font-bold text-salvia">Rol</label>
            <select value={rol} onChange={(e) => setRol(e.target.value as Rol)} className="campo text-base max-w-xs">
              {Object.entries(ETIQUETA_ROL).map(([valor, etiqueta]) => (
                <option key={valor} value={valor}>
                  {etiqueta}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold text-salvia">Acceso a sucursales</label>
            <ChecksSucursales sucursales={sucursales} seleccion={sucursalesSel} alCambiar={setSucursalesSel} />
          </div>
          <button
            disabled={guardando}
            onClick={guardar}
            className="btn-principal text-sm py-2 px-6"
          >
            {guardando ? 'Guardando…' : 'Guardar cambios'}
          </button>

          <div className="border-t border-salvia/10 pt-3">
            <label className="mb-1 block text-xs font-bold text-salvia">
              {persona.rol === 'dueno' ? 'Restablecer contraseña' : 'Restablecer PIN'}
            </label>
            <div className="flex gap-2">
              <input
                type={persona.rol === 'dueno' ? 'password' : 'text'}
                inputMode={persona.rol === 'dueno' ? undefined : 'numeric'}
                placeholder={persona.rol === 'dueno' ? 'Mínimo 6 caracteres' : '4 a 6 dígitos'}
                value={nuevaClave}
                onChange={(e) =>
                  setNuevaClave(persona.rol === 'dueno' ? e.target.value : e.target.value.replace(/\D/g, ''))
                }
                className="campo text-base max-w-xs"
              />
              <button
                disabled={guardando || !nuevaClave}
                onClick={restablecerClave}
                className="rounded-lg border border-nopal bg-nopal-claro px-4 py-2 text-sm font-bold text-nopal-oscuro hover:bg-nopal/20"
              >
                Restablecer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function Personal({ sucursales }: { sucursales: Sucursal[] }) {
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [cargando, setCargando] = useState(true);
  const [mostrarForm, setMostrarForm] = useState(false);

  async function cargar() {
    try {
      setCargando(true);
      setPersonas(await api<Persona[]>('/usuarios'));
    } catch (err) {
      console.error(err);
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    cargar();
  }, []);

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-titulo text-2xl font-extrabold text-tinta">Personal</h1>
          <p className="text-sm text-salvia">Quién tiene acceso, con qué rol y en qué sucursal</p>
        </div>
        <button
          onClick={() => setMostrarForm(!mostrarForm)}
          className="btn-principal self-start text-sm py-2 px-4"
        >
          {mostrarForm ? 'Cancelar' : '+ Nuevo Personal'}
        </button>
      </div>

      {mostrarForm && (
        <FormularioAlta
          sucursales={sucursales}
          alCrear={() => {
            setMostrarForm(false);
            cargar();
          }}
        />
      )}

      {cargando ? (
        <p className="py-12 text-center text-salvia">Cargando personal…</p>
      ) : personas.length === 0 ? (
        <div className="rounded-2xl border border-salvia/20 bg-white p-8 text-center text-salvia">
          <p className="text-base font-semibold">No hay personal registrado todavía.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {personas.map((p) => (
            <FilaPersonal key={p.id} persona={p} sucursales={sucursales} alCambiar={cargar} />
          ))}
        </div>
      )}
    </div>
  );
}
